// NOTIF-02 (VEK-121) — the fleet operator's entry point to the staff Telegram bot: one row under the drawer's
// user card (name + role). Drawn only for the FLEET_OPERATOR seat (primary role, like the sidebar matrix),
// only in the open drawer, and only while the staff bot is on. Unlinked: "Connect Telegram Bot" opens the
// same ConnectDialog /notifications opens. Linked: plain "Telegram connected" with no action — Disconnect
// stays on /notifications (owner gate D2, 2026-10-06). The row and the panel share the ['me', 'telegram']
// query, so the dialog's poll and the panel's Disconnect flip it without extra wiring.
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';

import { Send2, TickCircle } from 'iconsax-reactjs';

import { useMyTelegramStatus } from 'api/vektra/hooks';
import type { UserRole } from 'api/vektra/types';
import { isOperator } from 'utils/roleGate';

import { ConnectDialog } from './TelegramPanel';

interface Props {
  drawerOpen: boolean;
  role: string | undefined;
}

export default function TelegramDrawerRow({ drawerOpen, role }: Props) {
  const { t } = useTranslation();
  const operator = isOperator(role as UserRole | undefined);
  const { data: status } = useMyTelegramStatus();
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);

  if (!drawerOpen || !operator || !status?.botEnabled) return null;

  if (status.connected) {
    return (
      <ListItem disablePadding sx={{ mt: 0.5 }} data-testid="telegram-drawer-row-connected">
        <ListItemIcon sx={{ minWidth: 28, color: 'success.main' }}>
          <TickCircle size={18} variant="Bold" />
        </ListItemIcon>
        <ListItemText
          primary={t('notifications.telegram.drawer.connected', { defaultValue: 'Telegram connected' })}
          slotProps={{ primary: { variant: 'body2', color: 'text.secondary' } }}
        />
      </ListItem>
    );
  }

  return (
    <>
      <ListItem disablePadding sx={{ mt: 0.5 }}>
        <ListItemButton onClick={() => setOpen(true)} sx={{ px: 0.5, py: 0.5, borderRadius: 1 }} data-testid="telegram-drawer-row-connect">
          <ListItemIcon sx={{ minWidth: 28, color: 'primary.main' }}>
            <Send2 size={18} />
          </ListItemIcon>
          <ListItemText
            primary={t('notifications.telegram.drawer.connect', { defaultValue: 'Connect Telegram Bot' })}
            slotProps={{ primary: { variant: 'body2', color: 'primary.main' } }}
          />
        </ListItemButton>
      </ListItem>
      {/* Rendered conditionally: a Dialog whose `open` flips before its portal mounts leaks the body scroll lock. */}
      {open && <ConnectDialog onClose={close} />}
    </>
  );
}
